class SizeChartDrawer extends HTMLElement {
  private overlay!: HTMLElement | null;
  private drawer!: HTMLElement | null;
  private closeButton!: HTMLElement | null;
  private tabs!: NodeListOf<HTMLElement>;
  private navItems!: NodeListOf<HTMLElement>;
  private primaryNavItems!: NodeListOf<HTMLElement>;
  private primaryPanes!: NodeListOf<HTMLElement>;
  private tables!: NodeListOf<HTMLTableElement>;
  private unitToggles!: NodeListOf<HTMLElement>;
  private currentUnit: 'cm' | 'in' = 'cm';
  private contentBox!: HTMLElement | null;
  private handleKeyDown: (e: KeyboardEvent) => void;
  private handleSizeChartClick: (e: Event) => void;

  constructor() {
    super();
    this.handleKeyDown = this.onKeyDown.bind(this);
    this.handleSizeChartClick = this.onSizeChartLinkClick.bind(this);
  }

  connectedCallback(): void {
    this.overlay = this.querySelector("[data-drawer-overlay]");
    this.drawer = this.querySelector("[data-drawer-panel]");
    this.closeButton = this.querySelector("[data-drawer-close]");
    this.tabs = this.querySelectorAll(".tab");
    this.navItems = this.querySelectorAll(".chart--sub-nav__item");
    this.primaryNavItems = this.querySelectorAll("[data-primary-tab]");
    this.primaryPanes = this.querySelectorAll("[data-primary-index]");
    this.tables = this.querySelectorAll("table");
    this.unitToggles = this.querySelectorAll(".chart--values li");
    this.contentBox = this.querySelector(".size-chart--content");

    // Intercept .size-chart-link clicks in capture phase to prevent Colorbox
    document.addEventListener("click", this.handleSizeChartClick, true);
    document.addEventListener("keydown", this.handleKeyDown);

    this.closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });

    this.overlay?.addEventListener("click", () => {
      this.close();
    });

    // Primary tab navigation
    this.primaryNavItems.forEach((item, index) => {
      item.addEventListener("click", () => {
        this.activatePrimaryTab(index);
      });
    });

    // Sub-tab navigation
    this.navItems.forEach((navItem, index) => {
      navItem.addEventListener("click", () => {
        this.activateTab(index);
      });
    });

    // Unit toggle (CM / IN)
    this.unitToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const unit = toggle.textContent?.trim() as "cm" | "in";
        this.setUnit(unit);
      });
    });

    // Table cell hover highlighting
    this.setupTableHighlighting();

    // Strip jQuery Colorbox handlers that theme.js binds to .size-chart-link.
    // theme.js loads after this script (both deferred) and may also re-bind
    // via Shopify's deferred section loader, so we must keep stripping.
    this.stripColorboxHandlers();
    window.addEventListener("load", () => this.stripColorboxHandlers());
    document.addEventListener(
      "shopify:section:load",
      () => {
        // theme.js re-binds Colorbox inside onSectionLoad; strip after it runs
        requestAnimationFrame(() => this.stripColorboxHandlers());
      },
    );
  }

  disconnectedCallback(): void {
    document.removeEventListener("click", this.handleSizeChartClick, true);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  /** Remove jQuery Colorbox click handlers from every .size-chart-link */
  private stripColorboxHandlers(): void {
    const jq = (window as any).jQuery;
    if (!jq) return;
    jq(".size-chart-link").off("click");

    // Also close Colorbox if it somehow opened
    if (jq.colorbox) {
      jq.colorbox.close();
    }
  }

  private onSizeChartLinkClick(e: Event): void {
    const target = e.target as HTMLElement;
    const link = target.closest(".size-chart-link");
    if (link) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.open();
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.isOpen()) {
      this.close();
    }
  }

  private isOpen(): boolean {
    return this.drawer?.classList.contains("is-open") ?? false;
  }

  open(): void {
    this.activateTab(this.getDefaultTabIndex());
    this.drawer?.classList.add("is-open");
    this.overlay?.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.updateContentHeight();
  }

  private getDefaultTabIndex(): number {
    const tags = (this.dataset.productTags ?? "").split(",").map((t) => t.trim());
    if (tags.includes("apparel")) return 1;
    return 0;
  }

  close(): void {
    this.drawer?.classList.remove("is-open");
    this.overlay?.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  private activatePrimaryTab(index: number): void {
    this.primaryNavItems.forEach((item) => item.classList.remove("active"));
    this.primaryNavItems[index]?.classList.add("active");
    this.primaryPanes.forEach((pane, i) => {
      pane.classList.toggle("active", i === index);
    });
  }

  private activateTab(index: number): void {
    this.navItems.forEach((item) => item.classList.remove("active"));
    this.navItems[index]?.classList.add("active");

    this.tabs.forEach((tab, i) => {
      if (i === index) {
        tab.style.display = "";
        // Force reflow before adding class for transition
        tab.offsetHeight;
        tab.classList.add("active");
      } else {
        tab.classList.remove("active");
        setTimeout(() => {
          if (!tab.classList.contains("active")) {
            tab.style.display = "none";
          }
        }, 500);
      }
    });

    setTimeout(() => this.updateContentHeight(), 100);
  }

  private updateContentHeight(): void {
    if (!this.contentBox) return;
    let height = 0;
    this.tabs.forEach((tab) => {
      if (tab.classList.contains("active")) {
        height = tab.offsetHeight;
      }
    });
    if (height) {
      this.contentBox.style.height = height + "px";
    }
  }

  private setUnit(unit: "cm" | "in"): void {
    this.currentUnit = unit;
    this.tables.forEach((table) => {
      table.dataset.value = unit;
    });
    this.unitToggles.forEach((toggle) => {
      toggle.classList.toggle("active", toggle.textContent?.trim() === unit);
    });
  }

  private setupTableHighlighting(): void {
    const cells = this.querySelectorAll("td, th");
    let clearTimer: ReturnType<typeof setTimeout>;

    cells.forEach((cell) => {
      cell.addEventListener("mouseenter", () => {
        clearTimeout(clearTimer);
        const index = Array.from(cell.parentElement?.children ?? []).indexOf(
          cell,
        );
        const tr = cell.closest("tr");
        if (!tr) return;

        // Highlight entire row
        tr.querySelectorAll("td, th").forEach((c) => c.classList.add("active"));
        // Clear siblings
        const siblings = tr.parentElement?.querySelectorAll("tr") ?? [];
        siblings.forEach((row) => {
          if (row !== tr) {
            row.querySelectorAll("td, th").forEach((c) => {
              c.classList.remove("active");
            });
            // Highlight same column
            const colCell = row.children[index];
            colCell?.classList.add("active");
          }
        });
      });

      cell.addEventListener("mouseleave", () => {
        clearTimer = setTimeout(() => {
          cells.forEach((c) => c.classList.remove("active"));
        }, 100);
      });
    });
  }
}

customElements.define("size-chart-drawer", SizeChartDrawer);
